import * as React from "react";
import { useGetUserQuery } from "@/api/users-admin";
import { TableCell, TableRow } from "@/components/ui/table";
import { formatDateToShort, formatTimeToAmPm, toDayNameFormat } from "@/helpers/dates";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { EyeIcon } from "lucide-react";
import { ChronologyListRowStatus } from "@/views/chronology-list/components/rowStatus";
import { DocumentTextIcon, FolderIcon } from "@heroicons/react/24/outline";
import { Transaction } from "@/types/transaction";

interface Props {
  transaction: Transaction;
}

export const ChronologyListRow: React.FC<Props> = ({ transaction }) => {
  const { data: createdBy } = useGetUserQuery(transaction.owner_id);

  if (!transaction.file_progresses.length) {
    return null;
  }

  return (
    <TableRow className="hover:bg-gray-50/50">
      <TableCell className="whitespace-nowrap py-3 pl-6">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{formatDateToShort(transaction.created_at)}</span>
          <div className="flex items-center gap-1 text-sm text-desert-sand-600">
            <span>{toDayNameFormat(transaction.created_at)}</span>
            <span>·</span>
            <span>{formatTimeToAmPm(transaction.created_at)}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className="py-3">
        <div>
          <div className="mb-2 flex">
            {transaction.fse_type === "folder" ? (
              <FolderIcon className="mr-2 h-5 w-5 text-yellow-500" />
            ) : (
              <DocumentTextIcon className="mr-2 h-5 w-5 text-blue-500" />
            )}
            <span className="line-clamp-1 text-sm text-gray-500" title={transaction.fse_name}>
              {transaction.fse_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ChronologyListRowStatus status={transaction.progress === 1 ? "COMPLETED" : "IN_PROGRESS"} />
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden py-3 lg:table-cell">
        <div className="flex min-w-[280px] items-center gap-3">
          <Avatar className="h-8 w-8 overflow-hidden rounded-full border-2 border-white shadow-sm">
            <AvatarImage
              title={createdBy && `${createdBy.name} ${createdBy.surname}`}
              src={createdBy?.avatar ? createBase64Image(createdBy.avatar) : defaultUserAvatar}
              alt={createdBy && `${createdBy.name} ${createdBy.surname}`}
              className="rounded-full object-cover"
            />
          </Avatar>
          <div className="flex-col items-start">
            <span className="line-clamp-1 rounded-md text-sm font-medium text-gray-900">
              {createdBy?.name} {createdBy?.surname}
            </span>
            <span className="line-clamp-1 text-xs text-gray-500">{createdBy?.email}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap py-3 pr-6 text-right">
        <Link to={`/chronology/${transaction.fse_id}`} title="View chronology">
          <Button variant="secondary" size="sm" className="flex items-center gap-2 px-3 py-2 text-xs" type="button">
            <EyeIcon className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Chronology</span>
          </Button>
        </Link>
      </TableCell>
    </TableRow>
  );
};
